/**
 * Entonnoir d'activation. Les étapes viennent du serveur (`FUNNEL_STEPS`) :
 * ce composant ne les code pas en dur, pour qu'un changement de parcours
 * côté back-office n'impose pas un redéploiement du dashboard.
 *
 * On affiche DEUX pourcentages par étape et pas un seul :
 *  - le taux de passage depuis l'étape précédente, qui pointe la marche où l'on
 *    perd les gens ;
 *  - la part du sommet, qui dit combien il en reste au bout.
 * Ne montrer que le second ferait porter le chapeau à la dernière étape alors
 * que la fuite peut être trois marches plus haut.
 *
 * Passe bento (2026-08-24) : la piste derrière chaque barre passe de l'aplat
 * gris aux HACHURES. Un aplat se lisait comme une seconde valeur — « il y a
 * quelque chose ici, mais quoi ? » ; les hachures disent la perte.
 *
 * Corrigé dans la foulée : le texte d'une étape ne chevauche plus sa barre.
 * Il tient DANS la barre en blanc, ou se pose APRÈS elle en encre foncée sur la
 * piste. L'ancien seuil en pixels laissait de l'encre presque noire sur le
 * violet de la ligne 7 dès qu'une étape descendait sous ~20 % du sommet.
 */

import { useState } from 'react';
import { ChartFrame, HatchDefs, HATCH_FILL, useMeasuredWidth } from './chartUtils';
import { formatInt, formatPercent } from '../lib/format';

export interface FunnelRow {
  key: string;
  label: string;
  value: number;
  /** Taux de passage depuis l'étape précédente (0→1), `null` sur la première. */
  rate: number | null;
  color: string;
}

export interface FunnelProps {
  title: string;
  rows: FunnelRow[];
  emptyLabel?: string;
}

const STEP_H = 66;
/** Colonne de droite réservée au taux de passage — le texte ne l'envahit pas. */
const RATE_GUTTER = 96;

/**
 * Largeur approchée d'un texte SVG, en unités de `viewBox`.
 *
 * Approchée et non mesurée : `getComputedTextLength()` impose un rendu puis une
 * seconde passe de mise en page, pour décider d'un placement dont l'erreur
 * acceptable est de dix pixels. Le facteur est calibré sur DM Sans (0,56 em) et
 * DM Mono (0,60 em), et on arrondit vers le haut — se tromper en surestimant
 * sort le texte de la barre, ce qui reste lisible ; l'inverse ne l'est pas.
 */
function estimateWidth(text: string, perChar: number): number {
  return Math.ceil(text.length * perChar);
}

function describe(title: string, rows: FunnelRow[]): string {
  if (rows.length === 0) return `${title} : aucune donnée`;
  const first = rows[0];
  const last = rows[rows.length - 1];
  if (!first || !last) return `${title} : aucune donnée`;
  const end = first.value > 0 ? Math.round((last.value / first.value) * 100) : 0;
  // On nomme l'étape la plus coûteuse : c'est l'information actionnable.
  let worst = rows[1];
  for (const r of rows.slice(1)) {
    if (worst && r.rate !== null && worst.rate !== null && r.rate < worst.rate) worst = r;
  }
  const worstPart =
    worst && worst.rate !== null
      ? ` La plus grosse perte se produit avant « ${worst.label} » (${Math.round(worst.rate * 100)} % de passage).`
      : '';
  return `${title}. ${rows.length} étapes, de ${formatInt(first.value)} à ${formatInt(last.value)}, soit ${end} % au bout.${worstPart}`;
}

export function Funnel({ title, rows, emptyLabel }: FunnelProps): React.ReactElement {
  const [hover, setHover] = useState<number | null>(null);
  const [frameRef, vbWidth] = useMeasuredWidth<HTMLElement>();
  const height = rows.length * STEP_H + 6;
  const first = rows[0];
  const max = first ? first.value : 0;
  const left = 4;
  const right = vbWidth - 4;
  const width = right - left;
  const hovered = hover === null ? undefined : rows[hover];
  // Rayon aligné sur `--radius-block` : les barres appartiennent à la même
  // famille de formes que les cartes qui les contiennent.
  const RX = 14;

  return (
    <ChartFrame
      ariaLabel={describe(title, rows)}
      empty={rows.length === 0 || max === 0}
      emptyLabel={emptyLabel}
      frameRef={frameRef}
      onPointerLeave={() => setHover(null)}
      tooltip={
        hovered ? (
          <div className="chart-tip" style={{ left: '50%', top: `${(((hover ?? 0) * STEP_H) / height) * 100}%` }}>
            <span className="t-when">{hovered.label}</span>
            <span className="t-row">
              <i className="t-swatch" style={{ background: hovered.color }} />
              {formatInt(hovered.value)}
              {max > 0 ? ` · ${formatPercent(hovered.value / max)} du sommet` : ''}
            </span>
          </div>
        ) : undefined
      }
    >
      <svg viewBox={`0 0 ${vbWidth} ${height}`} role="img" aria-label={describe(title, rows)}>
        <HatchDefs />
        {rows.map((row, i) => {
          const y = i * STEP_H + 4;
          const w = max > 0 ? Math.max((row.value / max) * width, 6) : 0;
          const lost = row.rate !== null && row.rate < 1;
          const meta = `${formatInt(row.value)}${max > 0 ? ` · ${formatPercent(row.value / max)} du sommet` : ''}`;
          // Placement du texte : DANS la barre en blanc, ou APRÈS elle en encre
          // foncée sur la piste hachurée. Jamais à cheval.
          //
          // Un seuil unique en pixels (« blanc si la barre dépasse 230 ») laissait
          // le cas qui casse tout : une barre courte mais pas minuscule portait
          // du texte foncé POSÉ sur sa couleur — de l'encre presque noire sur le
          // violet de la ligne 7, illisible. On compare donc la longueur du texte
          // à celle de la barre, et on sort le texte quand il ne tient pas.
          const textW = Math.max(estimateWidth(row.label, 7.6), estimateWidth(meta, 7.2));
          const afterX = left + w + 14;
          // `RATE_GUTTER` : la colonne de droite est réservée au taux de passage.
          const fitsAfter = right - RATE_GUTTER - afterX >= textW;
          const inside = 18 + textW + 14 <= w || !fitsAfter;
          const textX = inside ? left + 18 : afterX;
          const textFill = inside ? 'var(--c-surface)' : 'var(--c-ink)';
          return (
            <g key={row.key} onPointerEnter={() => setHover(i)}>
              {/* La piste : ce qui a été perdu à cette étape, en hachures. */}
              <rect x={left} y={y} width={width} height={STEP_H - 14} rx={RX} fill={HATCH_FILL} />
              <rect
                x={left}
                y={y}
                width={width}
                height={STEP_H - 14}
                rx={RX}
                fill="none"
                stroke="var(--c-line)"
                strokeWidth={1}
              />
              <rect
                x={left}
                y={y}
                width={w}
                height={STEP_H - 14}
                rx={RX}
                fill={row.color}
                opacity={hover === null || hover === i ? 1 : 0.55}
              />
              <text x={textX} y={y + 22} style={{ fill: textFill, fontSize: 13.5, fontWeight: 600 }}>
                {row.label}
              </text>
              <text
                x={textX}
                y={y + 40}
                style={{ fill: textFill, fontSize: 12, opacity: 0.85, fontFamily: 'var(--font-mono)' }}
              >
                {meta}
              </text>
              {/* Taux de passage, posé à droite hors de la barre pour rester
                  lisible même quand la barre est très courte. */}
              <text
                x={right - 14}
                y={y + 32}
                textAnchor="end"
                style={{
                  // Quand la barre s'étend jusqu'à la droite (l'étape de départ,
                  // à 100 %), le taux se retrouve POSÉ sur la couleur : il doit
                  // alors passer en blanc pour rester lisible.
                  fill: left + w > right - 90 ? 'var(--c-surface)' : lost ? 'var(--c-red)' : 'var(--c-ink3)',
                  fontSize: 13,
                  fontFamily: 'var(--font-mono)',
                }}
              >
                {row.rate === null ? 'départ' : `→ ${formatPercent(row.rate)}`}
              </text>
            </g>
          );
        })}
      </svg>
    </ChartFrame>
  );
}
